<template>
    <div class="wrapper w-100 h-100 d-flex justify-center align-center">
        <v-card class="login-card" elevation="6" rounded="xl">
            <v-card-text class="pa-8 pa-sm-10">
                <!-- Logo -->
                <div class="text-center mb-6">
                    <router-link class="d-inline-flex align-center text-decoration-none" to="/">
                        <v-img :src="logoFull" :width="200" draggable="false"/>
                        <span class="font-weight-bold font-italic text-h4 text-primary ms-1">Go</span>
                    </router-link>
                </div>

                <h2 class="text-h5 font-weight-bold text-center mb-1">Reset your password</h2>
                <p class="text-body-2 text-medium-emphasis text-center mb-6">
                    Enter the code from your email and choose a new password
                </p>

                <v-form ref="form" v-model="formData.valid" validate-on="input lazy" @submit.prevent="submit">
                    <v-text-field v-model="formData.code" :rules="codeRules"
                                  autocomplete="one-time-code" autofocus color="primary"
                                  density="comfortable" label="6-digit code"
                                  inputmode="numeric" maxlength="6" placeholder="000000"
                                  prepend-inner-icon="mdi-shield-key-outline"
                                  rounded="lg" variant="outlined"
                                  @input="onCodeInput"/>

                    <v-text-field v-model="formData.password" :rules="[vRules.required, vRules.minLength(8)]"
                                  autocomplete="new-password" color="primary" density="comfortable"
                                  label="New Password" prepend-inner-icon="mdi-lock-outline"
                                  rounded="lg" type="password" variant="outlined"/>

                    <v-text-field v-model="formData.password_confirmation"
                                  :rules="[vRules.required, vRules.minLength(8), vRules.sameAs(formData.password, 'password')]"
                                  autocomplete="new-password" color="primary" density="comfortable"
                                  label="Confirm Password" prepend-inner-icon="mdi-lock-check-outline"
                                  rounded="lg" type="password" variant="outlined"/>

                    <v-btn :disabled="!formData.valid || processing" :loading="processing" block color="primary"
                           class="mb-4" rounded="lg" size="large" @click="submit">
                        Reset Password
                    </v-btn>

                    <p class="text-body-2 text-center mb-0">
                        Remember your password?
                        <a class="text-primary text-decoration-none font-weight-bold cursor-pointer"
                           @click="navigateTo('login')">Sign in</a>
                    </p>
                </v-form>
            </v-card-text>
        </v-card>

        <!-- Footer -->
        <div class="login-footer text-caption text-white">
            &copy; {{ new Date().getFullYear() }} SimpliGPS Go by NextEra IoT
        </div>
    </div>
</template>

<script setup>
import router from "@/router";
import {reactive, ref} from 'vue'
import {send} from "@/composables/apiClient";
import {showApiError} from "@/composables/useApiError";
import logoFull from "@/assets/images/logo-full.png"
import {useNavigation} from "@/composables/globals";
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore()
const processing = ref(false)
const {navigateTo} = useNavigation()
const formData = reactive({
    valid: false,
    code: null,
    password: null,
    password_confirmation: null,
})

const codeRules = [
    v => !!v || 'Enter the code we emailed you',
    v => /^\d{6}$/.test(v || '') || 'Enter the 6-digit numeric code',
]

const onCodeInput = () => {
    if (formData.code) {
        formData.code = formData.code.replace(/\D/g, '').slice(0, 6)
    }
}

const submit = () => {
    processing.value = true;
    send('put', `reset-password/${router.currentRoute.value.params.id}`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message)
            navigateTo('login')
        }).catch(function (errorResponse) {
            const validationErrors = errorResponse?.response?.data?.errors;
            if (validationErrors && typeof validationErrors === 'object') {
                Object.keys(validationErrors).forEach((key) => {
                    snackbar.showSnackbar(validationErrors[key][0], 'error');
                });
                return;
            }
            showApiError(snackbar, errorResponse, "Couldn't reset your password. Please try again.");
        }).finally(() => processing.value = false)
}
</script>

<style scoped>
.wrapper {
    background: url('@/assets/images/login-bg.png') rgba(38, 35, 90, 0.65);
    background-blend-mode: multiply;
    background-size: cover;
    background-position: center;
    position: relative;
}

.login-card {
    width: 100%;
    max-width: 440px;
    margin: 16px;
}

.login-footer {
    position: absolute;
    bottom: 16px;
    opacity: 0.8;
}
</style>
